import React, { useState, useEffect } from 'react';
import api from '../api';
import styles from '../styles/DiscordVerification.module.css';

const DiscordVerification = ({ user, onUpdate }) => {
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [discordUsername, setDiscordUsername] = useState(user?.discord_username || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await api.getDiscordVerificationStatus();
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Error loading verification status:', error);
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleStartVerification = async () => {
    if (!discordUsername.trim()) {
      showMessage('Please enter your Discord username', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.startDiscordVerification(discordUsername);
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
      if (onUpdate) onUpdate();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to start verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteVerification = async () => {
    if (!verificationCode.trim()) {
      showMessage('Please enter the verification code', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.completeDiscordVerification(verificationCode);
      showMessage(response.data.message, 'success');
      setVerificationCode('');
      await loadVerificationStatus();
      if (onUpdate) onUpdate();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to complete verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    setLoading(true);
    try {
      const response = await api.cancelDiscordVerification();
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
      if (onUpdate) onUpdate();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to cancel verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isVerified = verificationStatus?.isVerified;
  const hasPendingVerification = verificationStatus?.hasPendingVerification;

  return (
    <div className={styles.discordVerification}>
      <h4>Discord Account Verification</h4>

      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          {message}
        </div>
      )}

      {isVerified ? (
        <div className={styles.verifiedStatus}>
          <div className={styles.statusIcon}>✅</div>
          <div className={styles.statusText}>
            <strong>Discord account verified!</strong>
            <p>Your Discord account is linked and you'll receive pick reminders.</p>
            {verificationStatus.discordId && (
              <p className={styles.discordId}>Discord ID: {verificationStatus.discordId}</p>
            )}
          </div>
        </div>
      ) : hasPendingVerification ? (
        <div className={styles.pendingVerification}>
          <div className={styles.statusIcon}>⏳</div>
          <div className={styles.statusText}>
            <strong>Verification pending</strong>
            <p>Check your Discord DMs for a verification code from the LOTW bot, then enter it below to complete verification.</p>
          </div>

          <div className={styles.verificationForm}>
            <label htmlFor="verification-code">Enter verification code:</label>
            <input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="6-digit code"
              maxLength="6"
              disabled={loading}
            />
            <div className={styles.buttonGroup}>
              <button
                onClick={handleCompleteVerification}
                disabled={loading || !verificationCode.trim()}
                className={styles.primaryButton}
              >
                {loading ? 'Verifying...' : 'Complete Verification'}
              </button>
              <button
                onClick={handleCancelVerification}
                disabled={loading}
                className={styles.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.notVerified}>
          <div className={styles.statusIcon}>❌</div>
          <div className={styles.statusText}>
            <strong>Discord account not verified</strong>
            <p>Link your Discord account to receive pick reminders and notifications.</p>
          </div>

          <div className={styles.verificationForm}>
            <label htmlFor="discord-username">Discord Username:</label>
            <input
              id="discord-username"
              type="text"
              value={discordUsername}
              onChange={(e) => setDiscordUsername(e.target.value)}
              placeholder="YourDiscordUsername#1234"
              disabled={loading}
            />
            <button
              onClick={handleStartVerification}
              disabled={loading || !discordUsername.trim()}
              className={styles.primaryButton}
            >
              {loading ? 'Starting...' : 'Start Verification'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.helpText}>
        <p><strong>How it works:</strong></p>
        <ol>
          <li>Enter your Discord username (including the # and numbers if you have an older account)</li>
          <li>Click "Start Verification" - the bot will send you a DM with a 6-digit code</li>
          <li>Copy the 6-digit code from your Discord DM</li>
          <li>Return to this page and enter the code in the form below</li>
          <li>Your account will be verified and you'll receive pick reminders!</li>
        </ol>
        <p><em>Note: The bot must be able to send you DMs. Make sure your Discord privacy settings allow DMs from server members.</em></p>
      </div>
    </div>
  );
};

export default DiscordVerification;
